import type { Country, Locale, Segment } from "@/types";
import type { DocType } from "./documents";

/**
 * The country × segment matrix.
 *
 * Read-only. The phase specification asked for an editable table, and this is a
 * deliberate departure: the resolver in `legalService.ts` is the closest thing
 * in the system to a legal opinion written as code, a wrong cell means an
 * institution operating without an AVV, and as code it is reviewed, diffed and
 * covered by a test over every row. As a Firestore document it would be none of
 * those, and the mistake would be silent.
 *
 * What this screen adds instead is the part that actually lowers the risk: the
 * matrix is fetched from the backend — resolved by the same function the
 * session gate calls, never re-implemented here — and the preview beside it
 * renders one profile's exact resolved set against what is published.
 */

export interface Requirement {
  type: DocType;
  required: boolean;
  language: Locale;
}

export interface MatrixRow {
  country: Country;
  segment: Segment;
  requirements: Requirement[];
}

export type Cell = "required" | "reference" | "none";

export function cellOf(row: MatrixRow, type: DocType): Cell {
  const requirement = row.requirements.find((candidate) => candidate.type === type);
  if (!requirement) return "none";
  return requirement.required ? "required" : "reference";
}

export const CELL_KEY: Record<Cell, string> = {
  required: "legal.matrix.required",
  reference: "legal.matrix.reference",
  none: "legal.matrix.none",
};

/** The symbol in the cell. Never the only signal — every cell has a label too. */
export const CELL_MARK: Record<Cell, string> = {
  required: "●",
  reference: "○",
  none: "·",
};

export function countriesIn(rows: readonly MatrixRow[]): Country[] {
  const seen: Country[] = [];
  for (const row of rows) if (!seen.includes(row.country)) seen.push(row.country);
  return seen;
}

export function rowFor(
  rows: readonly MatrixRow[],
  country: Country,
  segment: Segment
): MatrixRow | undefined {
  return rows.find((row) => row.country === country && row.segment === segment);
}

/** The language a country's documents are served in, for the row summary. */
export function languageOf(row: MatrixRow): Locale | undefined {
  return row.requirements[0]?.language;
}

// ── the preview ──────────────────────────────────────────────────────────

/**
 * One profile, resolved and matched against what is published.
 *
 * An entry without a `docId` is a **gap**: something the resolver requires and
 * nobody has published. It is reported as ours rather than shown as a tick,
 * because a tick would mean an institution sailing through the session gate on
 * a document that does not exist.
 */
export interface PreviewEntry {
  type: DocType;
  required: boolean;
  language: Locale;
  docId?: string;
  version?: number;
  title?: string;
}

export interface Preview {
  country: Country;
  segment: Segment;
  entries: PreviewEntry[];
  /** Published for this profile and not resolved for it — a mis-scoped document. */
  unexpected: Array<{ docId: string; type: DocType }>;
}

export function gapsIn(preview: Preview): PreviewEntry[] {
  return preview.entries.filter((entry) => entry.required && !entry.docId);
}

export function isSound(preview: Preview): boolean {
  return gapsIn(preview).length === 0 && preview.unexpected.length === 0;
}
